import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { setDrawerOpen } from '../redux/actions/uiActions';
import { logoutUser } from '../redux/actions/authActions';
import { selectAttention } from '../redux/actions/fleetActions';

import { getUser } from '../api';
import { AlertTypes } from '../constants';
import { T, FONT_MONO } from '../constants/tokens';
import { configNavFor, canConfigure, viewsFor, isActive } from './navItems';

import Logo from '../images/LogoWhiteBackground.png';
import DefaultPortrait from '../images/DefaultPortrait.png';

/**
 * Hover and focus have to be classes — an inline style object cannot express
 * either.
 */
const useStyles = makeStyles({
  row: {
    transition: 'background 120ms ease',
    '&:hover': {
      background: T.railHover
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.ink,
      outlineOffset: -2
    }
  },
  disabled: {
    '&:hover': {
      background: 'transparent'
    }
  },
  account: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
    padding: 6,
    margin: -6,
    borderRadius: 6,
    textDecoration: 'none',
    color: T.ink,
    '&:hover': { background: T.railHover },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  },
  logout: {
    flex: 'none',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    height: 32,
    padding: '0 12px',
    borderRadius: 6,
    border: '1px solid ' + T.hairline,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    '&:hover': { background: T.surfaceSoft },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  }
});

/** The mono uppercase section label the design uses throughout the drawer. */
function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '16px',
        textTransform: 'uppercase',
        color: T.body,
        padding: '0 8px 8px'
      }}
    >
      {children}
    </div>
  );
}

SectionLabel.propTypes = { children: PropTypes.node };

/** The design's padlock, for configuration that only changes in setup mode. */
function Padlock() {
  return (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='none' stroke={T.body} strokeWidth='1.2' strokeLinecap='round' strokeLinejoin='round' style={{ flex: 'none' }} aria-label='Setup mode only'>
      <rect x='2.2' y='5.2' width='7.6' height='5.3' rx='1.2' />
      <path d='M4.2 5.2V3.9a1.8 1.8 0 0 1 3.6 0v1.3' />
    </svg>
  );
}

/**
 * The navigation drawer: the app's only navigation surface. The design has no
 * persistent rail on this shell, so the drawer is always an overlay, anchored
 * right, and the hamburger that opens it is always in the header.
 *
 * Top to bottom, as the design has it: Campus views, school status actions and
 * Configuration. The account card sits at the foot, as in the design's rail,
 * with who is signed in and a way to sign out. The day/night switch lives in
 * the header, so it is not repeated here.
 */
function NavDrawer({ open, onClose, userType, schoolStatus, setupMode, attentionCount, onLogout, onSetStatus, onOpenAlertPicker }) {
  const { pathname } = useLocation();
  const classes = useStyles();
  const [user, setUser] = useState(null);

  // Who is signed in, for the account card. Read when the drawer opens.
  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    getUser()
      .then((result) => {
        if (current) setUser(result);
      })
      .catch(() => {
        // The card falls back to the user type.
      });
    return () => {
      current = false;
    };
  }, [open]);

  if (!open) return null;

  const status = schoolStatus || AlertTypes.UNKNOWN;
  const views = viewsFor(userType);

  const rowStyle = (on, disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: on ? 600 : 500,
    lineHeight: 1.4,
    padding: '8px 12px',
    border: 0,
    borderRadius: 6,
    textDecoration: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    // The design marks the current row with weight, colour and its dot; the
    // soft fill is added so it still reads on the drawer's white.
    background: on ? T.surfaceSoft : 'transparent',
    color: disabled ? T.mutedSoft : (on ? T.ink : T.body),
    opacity: disabled ? 0.6 : 1
  });

  // The design's four: three school-wide statuses and the alert picker.
  const statusActions = [
    { key: 'EMERGENCY', label: 'Set Emergency for all rooms', color: AlertTypes.EMERGENCY.getColor().main, ring: AlertTypes.EMERGENCY.getColor().main, run: () => onSetStatus(AlertTypes.EMERGENCY) },
    { key: 'ALL_CLEAR', label: 'Set All Clear for all rooms', color: AlertTypes.ALL_CLEAR.getColor().main, ring: AlertTypes.ALL_CLEAR.getColor().main, run: () => onSetStatus(AlertTypes.ALL_CLEAR) },
    { key: 'CLEAR', label: 'Clear all statuses', color: 'transparent', ring: T.mutedSoft, run: () => onSetStatus(null) },
    { key: 'PICK', label: 'Set alert', color: AlertTypes.HOLD.getColor().main, ring: AlertTypes.HOLD.getColor().main, run: () => onOpenAlertPicker && onOpenAlertPicker() }
  ];

  const name = user?.getName?.()?.trim() || null;
  const email = user?.getEmail?.() || null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', justifyContent: 'flex-end' }}>
      <div
        style={{ position: 'absolute', inset: 0, background: T.scrim }}
        onClick={onClose}
      />

      <nav
        aria-label='Main navigation'
        style={{
          position: 'relative',
          width: 'min(300px, 86vw)',
          height: '100%',
          background: T.elevated,
          borderLeft: '1px solid ' + T.hairline,
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '24px 16px 0' }}>
          <img src={Logo} alt='SimulAlert' style={{ height: 32, width: 'auto', display: 'block' }} />
          <div style={{ flex: 1 }} />
          <button
            type='button'
            aria-label='Close navigation'
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              flex: 'none',
              borderRadius: 9999,
              border: '1px solid ' + T.hairline,
              background: T.elevated,
              color: T.ink,
              fontFamily: 'inherit',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            &#10005;
          </button>
        </div>

        <div style={{ minHeight: 0, overflowY: 'auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Campus views */}
          <div>
            <SectionLabel>Campus</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {views.map((view) => {
                const on = isActive(pathname, view.path);
                const count = view.key === 'overview' && attentionCount > 0 ? String(attentionCount) : '';

                if (!view.ready) {
                  return (
                    <span key={view.key} className={classes.disabled} style={rowStyle(false, true)} aria-disabled='true'>
                      <span style={{ width: 6, height: 6, borderRadius: 9999, flex: 'none', background: T.mutedSoft }} />
                      <span style={{ flex: 1, minWidth: 0 }}>{view.label}</span>
                      <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: T.mutedSoft }}>Soon</span>
                    </span>
                  );
                }

                return (
                  <Link key={view.key} to={view.path} onClick={onClose} className={classes.row} style={rowStyle(on, false)} aria-current={on ? 'page' : undefined}>
                    {/* The design dots the view you are on in the school's colour. */}
                    <span style={{ width: 6, height: 6, borderRadius: 9999, flex: 'none', background: on ? status.getColor().main : T.mutedSoft }} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{view.label}</span>
                    {count && <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.body }}>{count}</span>}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* School status actions */}
          {onSetStatus &&
            <div>
              <SectionLabel>School status actions</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {statusActions.map((action) => (
                  <button
                    key={action.key}
                    type='button'
                    onClick={() => {
                      action.run();
                      onClose();
                    }}
                    className={classes.row}
                    style={{
                      ...rowStyle(false, false),
                      color: T.ink,
                      minHeight: 40,
                      border: '1px solid ' + T.hairline,
                      background: T.elevated
                    }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: 9999, flex: 'none', background: action.color, border: '1px solid ' + action.ring }} />
                    <span style={{ flex: 1, minWidth: 0 }}>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>}

          {/* Configuration */}
          {canConfigure(userType) &&
            <div>
              <SectionLabel>Configuration</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {configNavFor(userType).map((item) => {
                  const on = isActive(pathname, item.path);
                  // The design locks the tables that only change in setup mode.
                  const locked = item.setupOnly === true && !setupMode;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      onClick={onClose}
                      className={classes.row}
                      style={{ ...rowStyle(on, false), gap: 8, fontWeight: on ? 600 : 400 }}
                    >
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                      {locked && <Padlock />}
                    </Link>
                  );
                })}
              </div>
            </div>}
        </div>

        {/* Account */}
        <div style={{ padding: 16, borderTop: '1px solid ' + T.hairline }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 12,
              border: '1px solid ' + T.hairline,
              borderRadius: 6,
              background: isActive(pathname, '/account/settings') ? T.surfaceSoft : T.elevated
            }}
          >
            <Link to='/account/settings' onClick={onClose} className={classes.account} aria-label='My account'>
              <img src={DefaultPortrait} alt='' style={{ width: 32, height: 32, borderRadius: 9999, objectFit: 'cover', flex: 'none' }} />
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name ?? 'My account'}
                </span>
                <span style={{ fontSize: 12, lineHeight: '16px', color: T.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {email ?? (userType?.name ? userType.name + ' user' : '')}
                </span>
              </span>
            </Link>
            <button type='button' className={classes.logout} onClick={onLogout}>
              Log out
            </button>
          </div>
        </div>
      </nav>
    </div>
  );
}

NavDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  userType: PropTypes.object,
  schoolStatus: PropTypes.object,
  setupMode: PropTypes.bool,
  attentionCount: PropTypes.number,
  onLogout: PropTypes.func,
  onSetStatus: PropTypes.func,
  onOpenAlertPicker: PropTypes.func
};

const mapStateToProps = (state) => ({
  open: state.ui.drawerOpen,
  userType: state.auth.userType,
  schoolStatus: state.status.schoolStatus,
  setupMode: state.setupMode.mode === true,
  // The same count the Overview pill shows.
  attentionCount: selectAttention(state).length
});

const mapDispatchToProps = (dispatch) => ({
  onClose: () => dispatch(setDrawerOpen(false)),
  onLogout: () => logoutUser(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(NavDrawer);
